<template>
  <div class="leo-operator-assistant">
    <el-drawer
      title="Leo SOC助手"
      :visible.sync="drawerOpen"
      size="420px"
      direction="rtl"
      append-to-body
    >
      <p class="muted assistant-hint">
        问后台操作、训练 SOP、何时找工程。已感知当前页面，会优先给本页相关说明。
      </p>
      <p v-if="pagePreface" class="assistant-page-preface">{{ pagePreface }}</p>
      <div v-if="clarifyOptions.length" class="assistant-clarify">
        <p class="assistant-clarify-prompt">{{ clarifyPrompt }}</p>
        <el-button
          v-for="(opt, idx) in clarifyOptions"
          :key="idx"
          size="mini"
          type="primary"
          plain
          @click="askClarifyOption(opt)"
        >{{ opt.label }}</el-button>
      </div>
      <div class="assistant-topics">
        <el-button
          v-for="t in quickTopics"
          :key="t.id"
          size="mini"
          plain
          :loading="loading && question === t.question"
          @click="askQuick(t.question)"
        >{{ t.labelZh }}</el-button>
      </div>
      <el-input
        v-model="question"
        type="textarea"
        :rows="3"
        placeholder="输入问题，例如：Composer shadow 怎么办？"
      />
      <div class="assistant-actions">
        <el-button
          type="primary"
          size="small"
          :loading="loading"
          @click="ask"
        >提问</el-button>
        <el-button
          type="danger"
          size="small"
          plain
          :disabled="!answer || loading"
          @click="onWrongAnswer"
        >回答不对</el-button>
      </div>
      <div v-if="answer" class="assistant-answer">
        <div class="assistant-answer-title">回答</div>
        <pre class="assistant-answer-body">{{ answer }}</pre>
        <p v-if="disclaimer" class="muted">{{ disclaimer }}</p>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import {
  fetchOperatorSupportTopics,
  askOperatorSupport,
  recordOperatorPageView,
  feedbackOperatorSupport,
  buildOperatorPageContext
} from '@/api/leoTrainingOperatorSupport'
import { leoOperatorAssistantBus } from '@/utils/leoOperatorAssistant'

export default {
  name: 'LeoTrainingOperatorAssistant',
  data () {
    return {
      drawerOpen: false,
      loading: false,
      question: '',
      answer: '',
      disclaimer: '',
      pagePreface: '',
      quickTopics: [],
      clarifyPrompt: '',
      clarifyOptions: [],
      clarifySlot: ''
    }
  },
  watch: {
    '$route.fullPath' () {
      this.onRouteChange()
    },
    drawerOpen (open) {
      if (open) {
        this.refreshTopics()
      }
    }
  },
  mounted () {
    leoOperatorAssistantBus.$on('open', this.open)
    leoOperatorAssistantBus.$on('close', this.close)
    this.reportPageContext()
  },
  beforeDestroy () {
    leoOperatorAssistantBus.$off('open', this.open)
    leoOperatorAssistantBus.$off('close', this.close)
  },
  methods: {
    pageContext () {
      return buildOperatorPageContext(this.$route, 'soc_admin', this.$store, this)
    },
    open () {
      this.drawerOpen = true
      this.$nextTick(() => this.refreshTopics())
    },
    close () {
      this.drawerOpen = false
    },
    onRouteChange () {
      this.reportPageContext()
      this.clearClarify()
      this.answer = ''
      this.disclaimer = ''
      if (this.drawerOpen) {
        this.refreshTopics()
      } else {
        this.pagePreface = ''
      }
    },
    async reportPageContext () {
      try {
        await recordOperatorPageView(this.pageContext())
      } catch (e) {
        // non-blocking
      }
    },
    async refreshTopics () {
      try {
        const res = await fetchOperatorSupportTopics(this.pageContext())
        const data = res && res.data
        this.quickTopics = (data && data.quickTopics) || []
        this.pagePreface = (data && data.pagePrefaceZh) || ''
      } catch (e) {
        this.quickTopics = []
        this.pagePreface = ''
      }
    },
    askQuick (q) {
      this.question = q
      this.ask()
    },
    askClarifyOption (opt) {
      const label = (opt && opt.label) || ''
      if (!label) return
      this.question = label
      this.ask()
    },
    clearClarify () {
      this.clarifyPrompt = ''
      this.clarifyOptions = []
      this.clarifySlot = ''
    },
    async ask () {
      const q = (this.question || '').trim()
      if (!q) {
        this.$message.warning('请输入问题')
        return
      }
      this.loading = true
      this.clearClarify()
      try {
        const res = await askOperatorSupport({
          question: q,
          pageContext: this.pageContext()
        })
        const data = res && res.data
        if (data && data.phase === 'clarify') {
          this.answer = ''
          this.clarifyPrompt = data.clarifyPrompt || data.answerZh || ''
          this.clarifySlot = data.clarifySlot || ''
          this.clarifyOptions = data.clarifyOptions || []
          this.disclaimer = data.disclaimerZh || ''
          if (data.pagePrefaceZh) {
            this.pagePreface = data.pagePrefaceZh
          }
          return
        }
        this.answer = (data && data.answerZh) || '暂无回答'
        this.disclaimer = (data && data.disclaimerZh) || ''
        if (data && data.pagePrefaceZh) {
          this.pagePreface = data.pagePrefaceZh
        }
      } catch (e) {
        this.$message.error(e.message || '提问失败')
      } finally {
        this.loading = false
      }
    },
    onWrongAnswer () {
      if (!this.answer) return
      const apologyHtml = [
        '<p style="margin:0 0 12px;line-height:1.6;">非常抱歉，Leo 智能系统仍在持续学习中；若本次回答未能帮到您，我们深表歉意。</p>',
        '<p style="margin:0 0 12px;line-height:1.6;">您的反馈已同步至 Leo 后台训练系统，将用于改进后续回答。</p>',
        '<p style="margin:0;line-height:1.6;">感谢您的耐心与支持，我们会努力做得更好。</p>'
      ].join('')
      this.$alert(apologyHtml, '感谢您的反馈', {
        confirmButtonText: '知道了',
        dangerouslyUseHTMLString: true,
        customClass: 'leo-operator-wrong-answer-dialog'
      }).then(() => this.sendFeedback(false)).catch(() => {})
    },
    async sendFeedback (helpful) {
      if (!this.answer) return
      try {
        await feedbackOperatorSupport({
          question: this.question,
          answerZh: this.answer,
          helpful: helpful,
          reason: helpful ? '' : 'user_marked_wrong',
          pageContext: this.pageContext()
        })
        if (helpful) {
          this.$message.success('感谢反馈')
        }
      } catch (e) {
        this.$message.error('反馈失败')
      }
    }
  }
}
</script>

<style scoped>
.assistant-hint {
  font-size: 13px;
  margin: 0 0 12px;
}
.assistant-page-preface {
  font-size: 13px;
  color: #409eff;
  margin: 0 0 12px;
  line-height: 1.5;
}
.assistant-clarify {
  margin-bottom: 12px;
  padding: 10px;
  background: #fef9e7;
  border-radius: 6px;
  border: 1px solid #faecd8;
}
.assistant-clarify-prompt {
  font-size: 13px;
  margin: 0 0 8px;
  line-height: 1.5;
}
.assistant-topics {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 12px;
}
.assistant-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}
.assistant-answer {
  margin-top: 16px;
}
.assistant-answer-title {
  font-weight: 600;
  margin-bottom: 8px;
}
.assistant-answer-body {
  white-space: pre-wrap;
  font-family: inherit;
  font-size: 13px;
  line-height: 1.55;
  margin: 0;
  padding: 10px;
  background: #f5f7fa;
  border-radius: 6px;
  max-height: 360px;
  overflow: auto;
}
</style>



// WEBPACK FOOTER //
// src/components/LeoKnowledge/LeoTrainingOperatorAssistant.vue